/**
 * "Save to client documents" (N7): files a saved assessment or letter
 * draft's Word file, exactly as the download serves it, in its client's
 * Documents. A second save files a new version superseding the first.
 *
 * Shows only when the draft carries a client and the viewer may file
 * clinical documents (write_clinical_notes, the documents upload gate).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './primitives/Button';
import { aiToolsApi } from '@/lib/api';
import { canWriteClinicalNotes } from '@/lib/capabilities';
import { clientDocumentsKey } from '@/lib/documents';
import { errorCodeOf, pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenant } from '@/lib/auth';

export const SAVE_TO_DOCUMENTS_LABEL = 'Save to client documents';

/** The site's own sentences for the refusals it can name. */
const REFUSAL_SENTENCES: Readonly<Record<string, string>> = {
  save_in_progress: 'The last copy is still saving. Wait a moment, then press Save again.',
  draft_has_no_client:
    "This draft is not linked to a client. Download it and upload it from the client's Documents.",
};

export interface SaveToClientDocumentsButtonProps {
  tenantSlug: string;
  draftId: string;
  /** The draft's client; null hides the button. */
  clientId: string | null;
  testId: string;
}

export function SaveToClientDocumentsButton({
  tenantSlug,
  draftId,
  clientId,
  testId,
}: SaveToClientDocumentsButtonProps) {
  const { whoami } = useTenant();
  const queryClient = useQueryClient();
  const contact = useSupportContact({ enabled: clientId !== null });
  const [saved, setSaved] = useState<'new' | 'replaced' | null>(null);
  const mutation = useMutation({
    mutationFn: () => aiToolsApi.saveAssessmentToDocuments(tenantSlug, draftId),
    onSuccess: (res) => {
      setSaved(res.version);
      void queryClient.invalidateQueries({ queryKey: clientDocumentsKey(tenantSlug, res.client_id) });
    },
  });

  if (clientId === null || !canWriteClinicalNotes(whoami)) return null;

  const code = mutation.isError ? errorCodeOf(mutation.error) : null;
  const failure = mutation.isError
    ? (code !== null ? REFUSAL_SENTENCES[code] : undefined) ?? pageWriteText(mutation.error, contact)
    : null;

  return (
    <span className="inline-flex flex-col gap-1">
      <Button
        type="button"
        variant="secondary"
        loading={mutation.isPending}
        disabled={mutation.isPending}
        data-testid={testId}
        onClick={() => {
          setSaved(null);
          mutation.mutate();
        }}
      >
        {SAVE_TO_DOCUMENTS_LABEL}
      </Button>
      {saved !== null && (
        <span className="text-xs text-ink-soft" data-testid={`${testId}-saved`} role="status">
          {saved === 'replaced'
            ? "Saved to this client's Documents as a new version."
            : "Saved to this client's Documents."}{' '}
          <Link className="underline" to={`/t/${tenantSlug}/clients/${clientId}?tab=documents`}>
            Open Documents
          </Link>
        </span>
      )}
      {failure !== null && (
        <span className="text-xs text-danger" data-testid={`${testId}-error`} role="alert">
          {failure}
        </span>
      )}
    </span>
  );
}
